import STRING from 'utils/string';
import { AmountChangePercentLabel } from 'utils/status-formatter';
import { cn } from 'lib/utils';
import { Icon } from 'components/ui/icon';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';

interface IProps {
  id: string;
  title: string;
  value: string | number;
  onClick: () => void;
  percentChange?: number | null;
  tooltip?: JSX.Element | string;
  isActive?: boolean;
  className?: string;
}

const CardTab = ({
  id,
  title,
  tooltip,
  value,
  percentChange,
  onClick,
  isActive,
  className = '',
}: IProps): JSX.Element => {
  return (
    <div
      id={id}
      className={cn(
        'rounded p-3 m-0 flex flex-col gap-4 justify-between',
        'shadow-card-hover bg-white box-content cursor-pointer relative',
        'hover:bg-light-green-bg active:bg-green-100',
        isActive && 'border-b-4 border-treez-green',
        className
      )}
      onClick={onClick}
    >
      {/* Triangle indicator for active state */}
      {isActive && (
        <div className="absolute -bottom-2.5 left-1/2 -ml-5 w-0 h-0 border-l-20 border-l-transparent border-r-20 border-r-transparent border-t-10 border-t-treez-green" />
      )}
      <div className="flex justify-between items-center">
        <span className="font-medium text-sm leading-5 text-gray-blue-60">
          {title}
        </span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                id={`${STRING.hyphenate(title)}-tooltip`}
                className="inline-flex items-center justify-center cursor-help"
              >
                <Icon name="info" size={14} className="text-dark-green-link" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="right" className="min-w-72">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="flex justify-between items-center">
        <span className="font-bold text-xl leading-7 text-gray-blue-90">
          {value}
        </span>
        <span>
          {(percentChange || percentChange === 0) && (
            <AmountChangePercentLabel value={percentChange} />
          )}
        </span>
      </div>
    </div>
  );
};

const CardTabNav = ({
  children,
  className = '',
  testId,
}: {
  children: React.ReactNode;
  className?: string;
  testId?: string;
}): JSX.Element => {
  return (
    <div id="card-tab" className={cn('', className)} data-testid={testId}>
      <div className="grid grid-flow-col grid-cols-3 gap-4 border-none mb-0">
        {children}
      </div>
    </div>
  );
};

export { CardTab, CardTabNav };
